/**
 * Subscriptions — moved out of the removed ecommerce module (SDK 2.0).
 *
 * Subscription state is the one thing the ecommerce module did that nothing
 * else replaces: `subscription_states` (MRR, plans, the Overview subscription
 * card) is fed by these events and by nothing else. Everything else in
 * ecommerce was dropped in favour of provider payments (Stripe / Lemon Squeezy
 * / Polar webhooks), which are the revenue source of truth.
 *
 * WIRE FORMAT: events go out as `type: "subscription"` → `?call=subscription`.
 * The event NAMES (`subscription_start`, …) are unchanged — they are what the
 * `mat_subscription_states` materialization matches on. SDK 1.x sent the same
 * names as `type: "ecommerce"`; flowgrid-core keeps accepting that for
 * installed 1.x clients.
 */
/**
 * @fileoverview Subscriptions - lifecycle & recurring revenue
 * @module features/subscriptions
 * 
 * @description
 * Track subscription lifecycle events for SaaS and subscription
 * businesses.
 * 
 * @useCases
 * - Track subscription signups
 * - Monitor subscription changes (upgrades/downgrades)
 * - Track cancellations and churn
 * - Calculate MRR/ARR metrics
 * - Analyze subscription cohorts
 * 
 * @metrics
 * - Monthly Recurring Revenue (MRR)
 * - Annual Recurring Revenue (ARR)
 * - Churn rate
 * - Expansion revenue
 * - Net revenue retention
 * 
 * @chartData
 * - Line chart: MRR over time
 * - Waterfall: MRR movement
 * - Bar chart: Revenue by plan
 * - Cohort: Subscription retention
 * 
 * @example
 * ```typescript
 * const subscriptions = new Subscriptions(webId, endpoint, apiKey);
 * 
 * // Track subscription start
 * await subscriptions.start({
 *   subscriptionId: 'sub_123',
 *   userId: 'user_456',
 *   plan: 'pro',
 *   mrr: { amount: 4900, currency: 'USD' },
 *   billingCycle: 'monthly'
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { MoneyValue, CurrencyCode, DateRangeFilter, TimeSeriesData } from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Subscription start configuration.
 */
export interface SubscriptionStartConfig {
  /** Subscription ID */
  subscriptionId: string;
  /** User ID */
  userId: string;
  /** Plan name */
  plan: string;
  /** Monthly recurring revenue */
  mrr: MoneyValue;
  /** Billing cycle */
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  /** Trial period (if applicable) */
  trialDays?: number;
  /** Coupon applied */
  coupon?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Subscription change configuration.
 */
export interface SubscriptionChangeConfig {
  /** Subscription ID */
  subscriptionId: string;
  /** User ID */
  userId: string;
  /** Previous plan */
  previousPlan: string;
  /** New plan */
  newPlan: string;
  /** Previous MRR */
  previousMrr: MoneyValue;
  /** New MRR */
  newMrr: MoneyValue;
  /** Change type */
  changeType: 'upgrade' | 'downgrade' | 'cross_grade';
}

/**
 * Subscription cancellation configuration.
 */
export interface SubscriptionCancelConfig {
  /** Subscription ID */
  subscriptionId: string;
  /** User ID */
  userId: string;
  /** Plan being cancelled */
  plan: string;
  /** Lost MRR */
  lostMrr: MoneyValue;
  /** Cancellation reason */
  reason?: string;
  /** Feedback provided */
  feedback?: string;
  /** Whether cancelled immediately or at period end */
  immediate: boolean;
}

/**
 * Subscription response from API.
 */
export interface SubscriptionResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    subscriptionId: string;
    tracked: boolean;
  };
  timestamp: string;
}

/**
 * Subscription analytics data.
 */
export interface SubscriptionAnalyticsData {
  /** Summary metrics */
  summary: {
    totalMrr: MoneyValue;
    totalArr: MoneyValue;
    activeSubscriptions: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
    churnRate: number;
    expansionMrr: MoneyValue;
    contractionMrr: MoneyValue;
    netMrrMovement: MoneyValue;
    netRevenueRetention: number;
    avgRevenuePerUser: MoneyValue;
  };
  /** MRR by plan */
  byPlan: Array<{
    plan: string;
    subscriptions: number;
    mrr: MoneyValue;
    percentage: number;
  }>;
  /** MRR waterfall */
  mrrMovement: {
    startingMrr: MoneyValue;
    newMrr: MoneyValue;
    expansionMrr: MoneyValue;
    contractionMrr: MoneyValue;
    churnedMrr: MoneyValue;
    endingMrr: MoneyValue;
  };
  /** MRR trend */
  mrrTrend: TimeSeriesData;
  /** Churn by reason */
  churnByReason: Array<{
    reason: string;
    count: number;
    lostMrr: MoneyValue;
  }>;
  /** Trial conversion rate */
  trialMetrics: {
    trialsStarted: number;
    trialsConverted: number;
    conversionRate: number;
  };
}

// ============================================================================
// SUBSCRIPTIONS CLASS
// ============================================================================

/**
 * Subscriptions - Track subscription lifecycle events.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides subscription tracking including:
 * - Subscription starts (new MRR)
 * - Plan changes (expansion/contraction)
 * - Cancellations (churned MRR)
 * - Renewals
 * - Trial conversions
 * 
 * @example
 * ```typescript
 * const subscriptions = new Subscriptions(webId, endpoint, apiKey);
 * 
 * // New subscription
 * await subscriptions.start({
 *   subscriptionId: 'sub_abc123',
 *   userId: 'user_456',
 *   plan: 'professional',
 *   mrr: { amount: 9900, currency: 'USD' },
 *   billingCycle: 'monthly',
 *   trialDays: 14
 * });
 * 
 * // Upgrade
 * await subscriptions.change({
 *   subscriptionId: 'sub_abc123',
 *   userId: 'user_456',
 *   previousPlan: 'professional',
 *   newPlan: 'enterprise',
 *   previousMrr: { amount: 9900, currency: 'USD' },
 *   newMrr: { amount: 29900, currency: 'USD' },
 *   changeType: 'upgrade'
 * });
 * ```
 */
export class Subscriptions extends FlowGridTransport {
  /**
   * Tracks subscription start.
   * 
   * @param config - Subscription start configuration
   * @returns Promise resolving to subscription response
   */
  async start(config: SubscriptionStartConfig): Promise<SubscriptionResponse> {
    return await this.init("subscription_start", {
      type: "subscription",
      subtype: "subscription_start",
      subscriptionId: config.subscriptionId,
      userId: config.userId,
      plan: config.plan,
      mrr: config.mrr,
      billingCycle: config.billingCycle,
      trialDays: config.trialDays,
      coupon: config.coupon,
      isTrialing: (config.trialDays || 0) > 0,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks subscription plan change.
   * 
   * @param config - Subscription change configuration
   * @returns Promise resolving to subscription response
   */
  async change(config: SubscriptionChangeConfig): Promise<SubscriptionResponse> {
    const mrrDiff = config.newMrr.amount - config.previousMrr.amount;
    
    return await this.init("subscription_change", {
      type: "subscription",
      subtype: "subscription_change",
      subscriptionId: config.subscriptionId,
      userId: config.userId,
      previousPlan: config.previousPlan,
      newPlan: config.newPlan,
      previousMrr: config.previousMrr,
      newMrr: config.newMrr,
      mrrChange: mrrDiff,
      changeType: config.changeType,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks subscription cancellation.
   * 
   * @param config - Cancellation configuration
   * @returns Promise resolving to subscription response
   */
  async cancel(config: SubscriptionCancelConfig): Promise<SubscriptionResponse> {
    return await this.init("subscription_cancel", {
      type: "subscription",
      subtype: "subscription_cancel",
      subscriptionId: config.subscriptionId,
      userId: config.userId,
      plan: config.plan,
      lostMrr: config.lostMrr,
      reason: config.reason,
      feedback: config.feedback,
      immediate: config.immediate,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks subscription renewal.
   * 
   * @param subscriptionId - Subscription ID
   * @param userId - User ID
   * @param mrr - Renewed MRR
   * @returns Promise resolving to subscription response
   */
  async renew(
    subscriptionId: string,
    userId: string,
    mrr: MoneyValue
  ): Promise<SubscriptionResponse> {
    return await this.init("subscription_renew", {
      type: "subscription",
      subtype: "subscription_renew",
      subscriptionId,
      userId,
      mrr,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks trial conversion to paid.
   * 
   * @param subscriptionId - Subscription ID
   * @param userId - User ID
   * @param plan - Converted to plan
   * @param mrr - New MRR
   * @returns Promise resolving to subscription response
   */
  async trialConverted(
    subscriptionId: string,
    userId: string,
    plan: string,
    mrr: MoneyValue
  ): Promise<SubscriptionResponse> {
    return await this.init("trial_converted", {
      type: "subscription",
      subtype: "trial_converted",
      subscriptionId,
      userId,
      plan,
      mrr,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks trial expiration without conversion.
   * 
   * @param subscriptionId - Subscription ID
   * @param userId - User ID
   * @returns Promise resolving to subscription response
   */
  async trialExpired(
    subscriptionId: string,
    userId: string
  ): Promise<SubscriptionResponse> {
    return await this.init("trial_expired", {
      type: "subscription",
      subtype: "trial_expired",
      subscriptionId,
      userId,
      timestamp: new Date().toISOString()
    });
  }

}

export default Subscriptions;
